/**
 * PortfolioPage — Govern tier
 *
 * Split-screen portfolio overview:
 *   Left pane: vertically-resizable map (top) + KPI grid table (bottom)
 *   Right pane: ProjectDetailPanel when a project is selected
 *
 * This is a focused extract of the original MyProjectsPage, retaining
 * only the split view (no standalone table, map-only, or cards modes).
 * The view switcher is removed — the split is the default and only layout.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { KpiMode } from '../../kpi-engine';

import KpiGridTable from '../../Components/KpiGridTable';
import ProjectMap from '../../Components/ProjectMap/ProjectMap';
import type { ProjectMapPoint } from '../../Components/ProjectMap/types';
import ResizableSplitPane from '../../Components/ResizableSplitPane/ResizableSplitPane';
import ProjectDetailPanel from '../ProjectDetail/ProjectDetailPanel';
// Task #213. Split-screen Mini-Project detail panel — mounts in the
// same right-pane slot as ProjectDetailPanel when the selected row is
// a Mini-Project (row._isMiniProject). Same close behavior, plus
// auto-opens a KPI modal when the user clicked a KPI dot in the grid
// (Skip's 2026-05-27 design, Option A).
import MiniProjectDetailPanel, {
  type MiniProjectAutoOpenKpi,
} from '../MiniProjectDetail/MiniProjectDetailPanel';
import GroupProgressPage from '../../Components/GroupProgressPage/GroupProgressPage';
import PortfolioSummaryPanel from '../../Components/PortfolioSummaryPanel/PortfolioSummaryPanel';

import { useFocus } from '../../state/FocusContext';
import { useProjectSelection } from '../../state/ProjectSelectionContext';
import { indicatorBucket, type IndicatorBucket } from '../../utils/indicatorBuckets';
import { tabIndexToField } from '../../state/focusRouting';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

type PortfolioPageProps = {
  data: any[];
  dataVersion?: number;
  kpiMode: KpiMode;
  onKpiModeChange: (m: KpiMode) => void;
  debugMode: boolean;
  onDebugModeChange: (v: boolean) => void;
  currentUserGuid: string;
  onUserChange: (userGuid: string) => void;
  authorizedCollectionGuids?: any;
  apiCollections?: Record<string, any>[];
  apiCollectionProjects?: Record<string, any>[];
  apiSchemaFields?: Record<string, any>[];
  apiMetadataValues?: Record<string, any>[];
  apiCustomEntities?: Record<string, any>[];
  onProjectConfigChanged: () => void;
  searchText: string;
  onSearchTextChange: (next: string) => void;
  apiProjectAcl?: Record<string, any>[];
  apiCommunityEntities?: Record<string, any>[];
  apiCommunityRelationships?: Record<string, any>[];
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function collectPrimitiveStrings(input: any, out: string[], depth: number, seen: Set<any>) {
  if (input == null) return;
  if (depth <= 0) return;
  const t = typeof input;
  if (t === 'string') { const s = input.trim(); if (s) out.push(s); return; }
  if (t === 'number' || t === 'boolean') { out.push(String(input)); return; }
  if (input instanceof Date && Number.isFinite(input.getTime())) { out.push(input.toISOString()); return; }
  if (Array.isArray(input)) { for (const v of input) collectPrimitiveStrings(v, out, depth - 1, seen); return; }
  if (t === 'object') {
    if (seen.has(input)) return;
    seen.add(input);
    for (const k of Object.keys(input)) {
      const lk = String(k || '').toLowerCase();
      if (lk.includes('geometry') || lk.includes('geojson') || lk.includes('wkt')) continue;
      collectPrimitiveStrings(input[k], out, depth - 1, seen);
    }
  }
}

function buildSearchBlob(row: any): string {
  try {
    const parts: string[] = [];
    const seen = new Set<any>();
    collectPrimitiveStrings(row, parts, 4, seen);
    if (row?.project) collectPrimitiveStrings(row.project, parts, 4, seen);
    return parts.join(' \n ').toLowerCase();
  } catch { return ''; }
}

const SPLIT_MIN_WIDTH = 900;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function PortfolioPage(props: PortfolioPageProps) {
  const { t } = useAppTranslation('pages');
  const {
    data,
    dataVersion,
    kpiMode,
    onKpiModeChange,
    debugMode,
    onDebugModeChange,
    currentUserGuid,
    onUserChange,
    authorizedCollectionGuids,
    apiCollections,
    apiCollectionProjects,
    apiSchemaFields,
    apiMetadataValues,
    apiCustomEntities,
    onProjectConfigChanged,
    searchText,
    onSearchTextChange,
  } = props;

  const gridKeyBase = String(dataVersion ?? 0);

  const { selectedProjectGuid, pulseProjectGuid, selectProject } = useFocus();
  const { mapColorMode, legendBucketFilter, setLegendBucketFilter } = useProjectSelection();

  /* ---- Collection selector (filters both map and grid) ---- */
  // DEV-967. Persist the page-level Collection selection so it survives
  // navigation away from /portfolio and back, and survives a page
  // refresh. Without this, the picker resets to "All My Projects" every
  // time the user returns to the page even after they've established
  // a working-set collection. Mirrors the persistence pattern in
  // KpiGridTable (its own LS_VIEW_KEY) but uses a separate key so the
  // two state stores don't compete — they're conceptually different
  // (table internal selection vs page-level filter scope).
  //
  // The validation effect below resets stale persisted GUIDs (e.g.,
  // collection was deleted while away from the page) so the picker
  // doesn't get stuck on an invalid selection.
  const LS_PORTFOLIO_COLLECTION_KEY = 'l4e_portfolio_collection_guid';
  const [selectedCollectionGuid, setSelectedCollectionGuid] = useState<string>(() => {
    try {
      const stored = window?.localStorage?.getItem(LS_PORTFOLIO_COLLECTION_KEY);
      if (stored && stored.trim()) return stored.trim();
    } catch {
      // localStorage unavailable (private mode, etc.) — fall through.
    }
    return '__ALL__';
  });

  // Persist on every change. Cheap write (single key, short value).
  useEffect(() => {
    try {
      window?.localStorage?.setItem(LS_PORTFOLIO_COLLECTION_KEY, selectedCollectionGuid);
    } catch {
      // ignore
    }
  }, [selectedCollectionGuid]);

  const collectionOptions = useMemo(() => {
    const allowed = Array.isArray(authorizedCollectionGuids)
      ? new Set((authorizedCollectionGuids as any[]).map((g: any) => String(g)))
      : null;
    const base = (apiCollections || [])
      .filter((c: any) => c?.is_active !== false)
      .filter((c: any) => !allowed || allowed.has(String(c.collection_guid)))
      .map((c: any) => ({
        value: String(c.collection_guid),
        label: String(c.collection_name),
      }));
    return [{ value: '__ALL__', label: t('portfolio.allProjects') }, ...base];
  }, [authorizedCollectionGuids, apiCollections, t]);

  // DEV-967. Validate the persisted selection against the current
  // collection list. If the user's last-selected collection was deleted,
  // archived, or the user lost access while they were away from the
  // page, the persisted GUID would point at nothing and the dropdown
  // would render with a stale value. Reset to '__ALL__' in that case
  // so the picker stays in a valid state. Skips validation while
  // collectionOptions is still empty (e.g., apiCollections hasn't
  // loaded yet on first render) so we don't reset prematurely.
  useEffect(() => {
    if (collectionOptions.length <= 1) return; // only '__ALL__' present — API still loading
    if (selectedCollectionGuid === '__ALL__') return;
    if (!collectionOptions.some((o) => o.value === selectedCollectionGuid)) {
      setSelectedCollectionGuid('__ALL__');
    }
  }, [collectionOptions, selectedCollectionGuid]);

  /** Set of project + mini-project GUIDs belonging to the selected collection
   *  (null = show all). DEV-1096 / Task #207: polymorphic — each row in
   *  collection_projects carries either a project_guid OR a mini_project_guid
   *  depending on member_type. Either identifier is added to the set so the
   *  downstream filter matches both Full-Project rows and MP rows in the
   *  Data array (MP rows use project_GUID = mini_project_guid). */
  const collectionMembershipSet = useMemo(() => {
    if (!selectedCollectionGuid || selectedCollectionGuid === '__ALL__') return null;
    const set = new Set<string>();
    for (const cp of (props.apiCollectionProjects || [])) {
      if (String(cp.collection_guid) !== selectedCollectionGuid) continue;
      const memberType = String(cp.member_type ?? 'project');
      if (memberType === 'mini_project' && cp.mini_project_guid) {
        set.add(String(cp.mini_project_guid));
      } else if (cp.project_guid) {
        set.add(String(cp.project_guid));
      }
    }
    return set;
  }, [selectedCollectionGuid, props.apiCollectionProjects]);

  /* ---- Selected project data row (for detail panel) ---- */
  const location = useLocation();
  const restoreGuid = (location.state as any)?.restoreProjectGuid as string | undefined;
  const restoreWorkSubTab = (location.state as any)?.workSubTab as string | undefined;
  const [selectedGuid, setSelectedGuid] = useState<string | null>(restoreGuid || null);
  const [activeKpiTabIndex, setActiveKpiTabIndex] = useState<number>(-1);
  const [tabRequestId, setTabRequestId] = useState(0);

  /* Auto-select project when returning from full-page view via restore */
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoreGuid && !restoredRef.current) {
      restoredRef.current = true;
      selectProject(restoreGuid, { pulse: true });
      setSelectedGuid(restoreGuid);
    }
  }, [restoreGuid, selectProject]);

  /* ---- Group selection (for GroupProgressPage) ---- */
  const [selectedGroupPayload, setSelectedGroupPayload] = useState<any | null>(null);
  const previousProjectRef = useRef<{ guid: string; tabIndex: number } | null>(null);

  const selectedProjectRow = useMemo(() => {
    if (!selectedGuid) return null;
    return (data || []).find((d: any) => String(d?.project_guid ?? '') === selectedGuid) ?? null;
  }, [data, selectedGuid]);

  /* ---- Responsive detection ---- */
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => {
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const useFullScreenDetail = containerWidth < SPLIT_MIN_WIDTH;

  /* ---- Vertical split resizing (map / grid) ---- */
  const [splitTopPct, setSplitTopPct] = useState<number>(55);
  const splitContainerRef = useRef<HTMLDivElement | null>(null);
  const splitDragRef = useRef<{ dragging: boolean; startY: number; startPct: number } | null>(null);

  /* ---- Map points ---- */
  const mapPoints = useMemo<ProjectMapPoint[]>(() => {
    return (data || [])
      .map((d: any) => {
        const p = d?.project || {};
        const rawLat = p?.location_lat;
        const rawLng = p?.location_lon;
        const lat = rawLat === '' || rawLat == null ? NaN : Number(rawLat);
        const lng = rawLng === '' || rawLng == null ? NaN : Number(rawLng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        if (lat === 0 && lng === 0) return null;
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
        return {
          project_guid: String(d?.project_guid ?? ''),
          name: String(p?.project_name ?? 'Untitled'),
          lat, lng,
          summary_indicator_color: d?.summary_indicator_color ?? null,
          schedule_indicator_color: d?.schedule_indicator_color ?? null,
          mission_indicator_color: d?.mission_indicator_color ?? null,
          finance_indicator_color: d?.finance_indicator_color ?? null,
          data_reliability_color: d?.data_reliability_color ?? null,
          // DEV-851: Project the row's _isCompletedProject flag so the map
          // renders the blue Complete state for closed projects in 'data' mode.
          _isCompletedProject: !!(d as any)?._isCompletedProject,
          // Task #207 Phase 2: discriminator for marker shape — MP rows
          // render as rounded squares so users can distinguish them from
          // Full-Project circle markers without changing color semantics.
          _isMiniProject: !!(d as any)?._isMiniProject,
        } as ProjectMapPoint;
      })
      .filter(Boolean) as ProjectMapPoint[];
  }, [data]);

  /* ---- Search / Filter ---- */
  const searchTerms = useMemo(() => {
    const raw = String(searchText || '').trim().toLowerCase();
    if (!raw) return [] as string[];
    return raw.split(/\s+/).filter(Boolean);
  }, [searchText]);

  const projectSearchIndex = useMemo(() => {
    const idx = new Map<string, string>();
    for (const row of data || []) {
      const guid = String(row?.project_guid ?? '');
      if (!guid) continue;
      idx.set(guid, buildSearchBlob(row));
    }
    return idx;
  }, [data]);

  const matchedProjectGuidSet = useMemo(() => {
    if (searchTerms.length === 0) return null;
    const set = new Set<string>();
    for (const row of data || []) {
      const guid = String(row?.project_guid ?? '');
      if (!guid) continue;
      const blob = projectSearchIndex.get(guid) || '';
      if (searchTerms.every((term) => blob.includes(term))) set.add(guid);
    }
    return set;
  }, [data, projectSearchIndex, searchTerms]);

  const bucketForRow = useCallback(
    (row: any, mode: string): IndicatorBucket => {
      const p = row?.project || row || {};
      // DEV-851: blue Complete-state override on the Data Reliability mode.
      const isCompleted = !!(row as any)?._isCompletedProject;
      switch (mode) {
        case 'schedule': return indicatorBucket(p.schedule_indicator_color);
        case 'finance': return indicatorBucket(p.finance_indicator_color);
        case 'mission': return indicatorBucket(p.mission_indicator_color);
        case 'data':
          if (isCompleted) return 'completed';
          return indicatorBucket(p.data_reliability_color);
        default: return indicatorBucket(p.summary_indicator_color);
      }
    },
    []
  );

  const filteredMapPoints = useMemo(() => {
    let pts = mapPoints;
    if (collectionMembershipSet) {
      pts = pts.filter((p: any) => collectionMembershipSet.has(String(p?.project_guid ?? '')));
    }
    if (matchedProjectGuidSet) {
      pts = pts.filter((p: any) => matchedProjectGuidSet.has(String(p?.project_guid ?? '')));
    }
    return pts;
  }, [mapPoints, collectionMembershipSet, matchedProjectGuidSet]);

  const filteredTableData = useMemo(() => {
    let rows = data || [];
    if (collectionMembershipSet) {
      rows = rows.filter((r: any) => collectionMembershipSet.has(String(r?.project_guid ?? '')));
    }
    if (matchedProjectGuidSet) {
      rows = rows.filter((r: any) => matchedProjectGuidSet.has(String(r?.project_guid ?? '')));
    }
    if (legendBucketFilter) {
      rows = rows.filter((r: any) => bucketForRow(r, mapColorMode) === legendBucketFilter);
    }
    return rows;
  }, [bucketForRow, collectionMembershipSet, data, legendBucketFilter, mapColorMode, matchedProjectGuidSet]);

  /* ---- Selection → open detail panel ---- */
  const handleSelectProject = useCallback(
    (item: any, tabIndex: number = 0) => {
      if (item?.__type === 'group') {
        if (selectedGuid) {
          previousProjectRef.current = { guid: selectedGuid, tabIndex: activeKpiTabIndex };
        } else {
          previousProjectRef.current = null;
        }
        setSelectedGroupPayload(item);
        setSelectedGuid(null);
        setActiveKpiTabIndex(tabIndex);
        return;
      }

      const guid = String(item?.project_guid ?? '');
      if (!guid) return;
      selectProject(guid, { pulse: true });
      setSelectedGuid(guid);
      setSelectedGroupPayload(null);
      setActiveKpiTabIndex(tabIndex);
      setTabRequestId((n) => n + 1);
    },
    [selectProject, selectedGuid, activeKpiTabIndex]
  );

  const handleGroupBack = useCallback(() => {
    const prev = previousProjectRef.current;
    setSelectedGroupPayload(null);
    if (prev) {
      selectProject(prev.guid, { pulse: true });
      setSelectedGuid(prev.guid);
      setActiveKpiTabIndex(prev.tabIndex);
    } else {
      setSelectedGuid(null);
      setActiveKpiTabIndex(-1);
    }
    previousProjectRef.current = null;
  }, [selectProject]);

  const handleGroupSelectProject = useCallback(
    (item: any, tabIndex: number) => {
      const guid = String(item?.project_guid ?? item?.project?.project_guid ?? '');
      if (!guid) return;
      selectProject(guid, { pulse: true });
      setSelectedGuid(guid);
      setSelectedGroupPayload(null);
      setActiveKpiTabIndex(tabIndex);
      setTabRequestId((n) => n + 1);
    },
    [selectProject],
  );

  const handleMapClick = useCallback(
    (guid: string, _mode: string) => {
      selectProject(guid, { pulse: true });
      setSelectedGuid(guid);
      setSelectedGroupPayload(null);
      setActiveKpiTabIndex(-1);
      setTabRequestId((n) => n + 1);

      if (legendBucketFilter) {
        const row = (data || []).find((d: any) => String(d?.project_guid ?? '') === guid);
        if (row && bucketForRow(row, mapColorMode) !== legendBucketFilter) {
          setLegendBucketFilter(null);
        }
      }
    },
    [selectProject, legendBucketFilter, data, bucketForRow, mapColorMode, setLegendBucketFilter]
  );

  const handleCloseDetail = useCallback(() => {
    setSelectedGuid(null);
    setActiveKpiTabIndex(-1);
  }, []);

  const handleDetailKpiTabChange = useCallback((idx: number) => {
    setActiveKpiTabIndex(idx);
  }, []);

  /* ---- Vertical split drag ---- */
  const beginDrag = (e: React.MouseEvent) => {
    const container = splitContainerRef.current;
    if (!container) return;
    splitDragRef.current = { dragging: true, startY: e.clientY, startPct: splitTopPct };
    e.preventDefault();
  };

  const onMouseMove = useCallback((e: MouseEvent) => {
    const d = splitDragRef.current;
    const container = splitContainerRef.current;
    if (!d?.dragging || !container) return;
    const rect = container.getBoundingClientRect();
    const deltaY = e.clientY - d.startY;
    const deltaPct = (deltaY / Math.max(1, rect.height)) * 100;
    setSplitTopPct(Math.max(20, Math.min(80, d.startPct + deltaPct)));
  }, []);

  const endDrag = useCallback(() => {
    if (splitDragRef.current) splitDragRef.current.dragging = false;
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', endDrag);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', endDrag);
    };
  }, [onMouseMove, endDrag]);

  /* ------------------------------------------------------------------ */
  /*  Left pane: search bar + map/grid vertical split                    */
  /* ------------------------------------------------------------------ */

  const leftPane = (
    <>
      {/* Vertical split: map top, KPI grid bottom.
           Menu bar floats over the map with semi-transparent idle / opaque-on-hover.
           At narrow widths (useFullScreenDetail), hide map + floating toolbar to
           give the KPI grid full vertical space. */}
      <div className="col-content" style={{ minHeight: 0, position: 'relative' }}>
        {/* Floating menu bar over the map (desktop only).
            At narrow widths the map is hidden and Collection + Search move
            into the KpiGridTable's built-in toolbar (hideSearch/hideCollection=false). */}
        {!useFullScreenDetail && (
          <div
            className="l4e-myprojects-viewbar l4e-myprojects-viewbar--floating"
            style={{ zIndex: 1001 }}
          >
            <div className="l4e-myprojects-viewbar-left">
              {/* Collection selector */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
                {t('portfolio.collection')}
                <select
                  value={selectedCollectionGuid}
                  onChange={(e) => setSelectedCollectionGuid(e.target.value)}
                  style={{
                    fontSize: 13,
                    padding: '2px 6px',
                    borderRadius: 4,
                    border: '1px solid var(--border-default)',
                    background: 'var(--bg-elevated)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {collectionOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>

              {/* Search */}
              <div className="l4e-projectsearch">
                <svg width="16" height="16" viewBox="0 0 24 24"><path d="M10 2a8 8 0 105.3 14l4.2 4.2 1.4-1.4-4.2-4.2A8 8 0 0010 2zm0 2a6 6 0 110 12 6 6 0 010-12z" /></svg>
                <input
                  className="l4e-projectsearch-input"
                  value={searchText}
                  onChange={(e) => onSearchTextChange(e.target.value)}
                  placeholder={t('portfolio.filterProjects')}
                  spellCheck={false}
                />
                {searchTerms.length > 0 && (
                  <button className="l4e-projectsearch-clear" onClick={() => onSearchTextChange('')} title={t('portfolio.clear')} type="button">×</button>
                )}
              </div>
            </div>
          </div>
        )}
        <div ref={splitContainerRef} className="l4e-split-vertical">
          {!useFullScreenDetail && (
            <>
              <div style={{ height: `${splitTopPct}%`, minHeight: 0 }} className="l4e-portfolio-map-pane">
                <ProjectMap
                  points={filteredMapPoints as any}
                  onProjectClick={handleMapClick}
                  showColorBy={true}
                  selectedProjectGuid={selectedProjectGuid || undefined}
                  pulseProjectGuid={pulseProjectGuid || undefined}
                />
              </div>
              <div className="l4e-resize-handle" onMouseDown={beginDrag} />
            </>
          )}
          <div style={{ height: useFullScreenDetail ? '100%' : `${100 - splitTopPct}%`, minHeight: 0 }}>
            <KpiGridTable
              key={`kgt-${gridKeyBase}-portfolio`}
              data={filteredTableData}
              onSelect={handleSelectProject}
              externalFocusKpiField={tabIndexToField(activeKpiTabIndex >= 0 ? activeKpiTabIndex : 0)}
              externalFocusRowId={selectedProjectGuid || ''}
              kpiMode={kpiMode}
              onKpiModeChange={onKpiModeChange}
              debugMode={debugMode}
              onDebugModeChange={onDebugModeChange}
              currentUserGuid={currentUserGuid}
              onUserChange={onUserChange}
              authorizedCollectionGuids={authorizedCollectionGuids}
              apiCollections={apiCollections}
              apiCollectionProjects={apiCollectionProjects}
              apiSchemaFields={apiSchemaFields}
              apiMetadataValues={apiMetadataValues}
              apiCustomEntities={apiCustomEntities}
              searchText={searchText}
              onSearchTextChange={onSearchTextChange}
              hideSearch={!useFullScreenDetail}
              hideCollection={!useFullScreenDetail}
              // DEV-920 Layer 2 follow-up. Pass the page-level collection
              // selection down so the table's schema lookup (Group By
              // hierarchies, schema-driven fields) reflects what the user
              // picked in the toolbar at the top of the map. Without this,
              // selecting a collection filters which projects appear in
              // `data` but the table's schema_guid stays stuck on whatever
              // was last persisted in its OWN selectedCollectionGuid (often
              // "__ALL__"), so Group By never offers the hierarchies
              // declared on the actually-selected collection's schema.
              //
              // Note: a stale HMR cache initially made this look like it
              // was triggering a Leaflet crash; a full Vite restart cleared
              // the false positive. The Leaflet markercluster stale-marker
              // warning that remains is a real pre-existing race, now
              // swallowed by a try/catch in ProjectMap (DEV-965).
              selectedCollectionGuid={selectedCollectionGuid}
            />
          </div>
        </div>
      </div>
    </>
  );

  /* ------------------------------------------------------------------ */
  /*  Right pane: detail panel                                           */
  /* ------------------------------------------------------------------ */

  const detailPanel = selectedGroupPayload ? (
    <GroupProgressPage
      groupPayload={selectedGroupPayload}
      onBack={handleGroupBack}
      onSelectProject={handleGroupSelectProject}
      kpiCategory={
        activeKpiTabIndex === 2 ? 'finance'
        : activeKpiTabIndex === 3 ? 'mission'
        : activeKpiTabIndex === 4 ? 'data'
        : activeKpiTabIndex === 0 ? 'summary'
        : 'schedule'
      }
      kpiCategoryLabel={
        activeKpiTabIndex === 2 ? 'Budget'
        : activeKpiTabIndex === 3 ? 'Mission'
        : activeKpiTabIndex === 4 ? 'Data Reliability'
        : activeKpiTabIndex === 0 ? 'Summary'
        : 'Schedule'
      }
    />
  ) : selectedProjectRow ? (
    // Task #213. Polymorphic right pane: MP rows route to the slim
    // MiniProjectDetailPanel; FP rows continue to use ProjectDetailPanel
    // unchanged. The `_isMiniProject` flag is set in App.tsx by the MP
    // append in the Data aggregator.
    (selectedProjectRow as any)._isMiniProject ? (
      <MiniProjectDetailPanel
        miniProjectGuid={String(selectedProjectRow?.project_guid ?? '')}
        currentUserGuid={currentUserGuid}
        onClose={handleCloseDetail}
        // Map the KPI tab index back to the MP modal name. The grid's
        // tab index convention (set in handleSelectProject): 0=Summary,
        // 1=Schedule, 2=Budget, 3=Mission, 4=Data Reliability. Summary
        // routes to the StatusWriter (Lead) / StatusReader (non-Lead)
        // flow per Skip's 2026-05-27 design — clicking the Summary dot
        // means "I want to act on (or read) today's status." -1 (row
        // body / name click) → null = land on Progress with no modal.
        autoOpenKpi={
          activeKpiTabIndex === 0 ? 'summary'
          : activeKpiTabIndex === 1 ? 'schedule'
          : activeKpiTabIndex === 2 ? 'budget'
          : activeKpiTabIndex === 3 ? 'mission'
          : activeKpiTabIndex === 4 ? 'dataReliability'
          : (null as MiniProjectAutoOpenKpi)
        }
        autoOpenKpiRequestId={tabRequestId}
      />
    ) : (
      <ProjectDetailPanel
        projectRow={selectedProjectRow}
        currentUserGuid={currentUserGuid}
        debugMode={debugMode}
        onProjectConfigChanged={onProjectConfigChanged}
        onClose={handleCloseDetail}
        kpiTabIndex={activeKpiTabIndex}
        tabRequestId={tabRequestId}
        onKpiTabChange={handleDetailKpiTabChange}
        viewMode="portfolio"
        initialWorkSubTab={restoreWorkSubTab as any}
      />
    )
  ) : (
    <PortfolioSummaryPanel />
  );

  /* ------------------------------------------------------------------ */
  /*  Responsive: full-screen detail takeover on narrow screens          */
  /* ------------------------------------------------------------------ */

  if (useFullScreenDetail && selectedProjectRow) {
    return (
      <div ref={containerRef} className="content" style={{ flexDirection: 'column' }}>
        {detailPanel}
      </div>
    );
  }

  /* Narrow viewport, no project selected → give the left pane (map + grid)
     full width instead of wasting half the screen on an empty detail panel. */
  if (useFullScreenDetail) {
    return (
      <div ref={containerRef} className="content" style={{ flexDirection: 'column' }}>
        <section className="col col-left" style={{ flex: 1, maxWidth: '100%', height: '100%' }}>
          {leftPane}
        </section>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Render: horizontal split (left=map+grid, right=detail)             */
  /* ------------------------------------------------------------------ */

  return (
    <div ref={containerRef} className="content" style={{ flexDirection: 'column' }}>
      <ResizableSplitPane
        left={
          <section className="col col-left" style={{ flex: 1, maxWidth: '100%', height: '100%' }}>
            {leftPane}
          </section>
        }
        right={detailPanel}
        initialRatio={0.55}
        minWidth={300}
      />
    </div>
  );
}
